import axios from 'axios'
import fileDownload from 'js-file-download'
import Api from 'rest-fetcher-redux'
import {
  captureSentryMessageError,
  captureSentryMessageWarning,
} from '~/legacy/utils'
import { Segment } from './hooks/segment'

const logSaveListing = (segment, result, building, surveyId) => {
  if (result && result.id) {
    const additionalFields = result.custom_fields
    Segment.trackCreateListing({
      listingId: result.id,
      buildingId: building.id,
      numAdditionalFields: additionalFields
        ? Object.keys(additionalFields).length
        : 0,
      surveyId,
    })
    if (surveyId) {
      Segment.trackAddListingToSurvey(surveyId, result.id)
    }
  }
}

export const ApiHelper = {
  // Update the survey listing status
  updateSurveyListingStatus: ({
    surveyListingId,
    newStatus,
    colors,
    statusChange,
  }) => {
    const promise = Api.updateSurveyListingStatus({
      id: surveyListingId,
      body: { status: newStatus, colors },
    })

    promise.then(() => {
      Segment.trackSurveyListingStatus(statusChange)
    })

    return promise
  },

  updateSurveyListingStatuses: ({ surveyListings, colors }) => {
    const promise = Api.updateSurveyListingStatuses({
      body: { survey_listings: surveyListings, colors },
    })

    // TODO: Batch request?
    promise.then(() => {
      surveyListings.forEach((surveyListing) =>
        Segment.trackSurveyListingStatus(surveyListing.status_change)
      )
    })

    return promise
  },

  addListingsBulk: ({
    listings,
    bulkImportCsvFileId = null,
    surveyId = null,
    customFields,
  }) => {
    const promise = Api.saveListingsBulk({
      body: {
        listings,
        bulk_import_csv_file_id: bulkImportCsvFileId,
        survey_id: surveyId,
        custom_fields: customFields,
      },
    })

    // TODO: make batch call
    promise.then((results) => {
      if (results) {
        results.forEach((result) =>
          logSaveListing(Segment, result, result.building.id, surveyId)
        )
      }
    })

    return promise
  },

  exportSurvey: ({
    survey,
    html,
    cssString = '',
    format = 'letter',
    landscape = false,
  }) =>
    axios
      .post(
        `${process.env.APP_API_URL}/surveys/${survey.id}/export/`,
        {
          html,
          cssString,
          format,
          landscape,
        },
        {
          responseType: 'blob',
          headers: {
            Authorization: Api.baseOptions.headers.Authorization,
          },
        }
      )
      .then((res) =>
        fileDownload(res.data, `${survey.name.replaceAll(' ', '')}.pdf`)
      )
      .catch(() => {}),

  uploadUserAvatar: ({ file, userId }) => {
    const formData = new FormData()
    const header = Api.baseOptions.headers
    formData.append('file', file, file.name)
    formData.append('user_id', userId)
    const promise = fetch(`${process.env.APP_API_URL}/user_avatar_image/`, {
      method: 'POST',
      headers: {
        Authorization: header.Authorization,
      },
      body: formData,
    }).then((response) => response.json())

    return promise
  },

  removeUserAvatar: ({ fileId }) => {
    const header = Api.baseOptions.headers
    const promise = fetch(
      `${process.env.APP_API_URL}/user_avatar_image/${fileId}/`,
      {
        method: 'DELETE',
        headers: {
          Authorization: header.Authorization,
        },
      }
    )

    return promise
  },

  updloadCompanySkinFaviconImage: ({ file }) => {
    const formData = new FormData()
    const header = Api.baseOptions.headers
    formData.append('file', file, file.name)
    const promise = fetch(
      `${process.env.APP_API_URL}/company_skin_favicon_image/`,
      {
        method: 'POST',
        headers: {
          Authorization: header.Authorization,
        },
        body: formData,
      }
    ).then((response) => response.json())

    return promise
  },

  updateUserData: (data, userId, password) =>
    Api.saveAccountSettings({
      id: userId,
      body: {
        ...data,
        password,
      },
    }),

  createBroker: (userFields = {}, logoFields = {}, file) => {
    const formData = new FormData()
    if (file) {
      formData.append('file', file, file.name)
    }
    Object.entries({
      ...userFields,
      ...logoFields,
    }).forEach(([key, value]) => formData.append(key, value))

    return fetch(`${process.env.APP_API_URL}/useradmin/create_broker/`, {
      method: 'POST',
      headers: {
        Authorization: Api.baseOptions.headers.Authorization,
      },
      body: formData,
    }).then((response) =>
      Promise.all([Promise.resolve(response), response.json()])
    )
  },

  uploadPropertyAsset: (file) => {
    const formData = new FormData();
    formData.append('file', file);

    return axios.post(
      `${process.env.APP_API_URL}/assets/upload_property_asset/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: Api.baseOptions.headers.Authorization,
        },
      }
    )
      .then(response => response.data)
      .catch(error => {
        console.error('Error uploading property asset:', error);
        throw error;
      });
  },

  extractPropertyData: ({ filename }) => {
    const makeRequest = async (retryCount = 0, startTime = Date.now()) => {
      try {
        const response = await axios.get(
          `${process.env.APP_API_URL}/assets/1/extract_property_data/?uploaded_file=${encodeURIComponent(filename)}`,
          {
            headers: {
              Authorization: Api.baseOptions.headers.Authorization,
            },
          }
        );

        if (response.data) {
          // Check if no properties were found
          if (response.data.results?.length === 0) {
            const failures = response.data.failures || [];
            const failureCount = failures.length;
            const displayedFailures = failures.slice(0, 3);
            
            console.group('Property Extraction Warnings');
            displayedFailures.forEach(f => console.log(`* ${f}`));
            if (failureCount > 3) {
              console.log(`* ...and ${failureCount - 3} other failures`);
            }
            console.groupEnd();
            
            const warnings = failures.map(f => `* ${f}`).join('\n') || 'No properties found';
            throw new Error(`No properties extracted:\n${warnings}`);
          }
          
          return {
            results: response.data.results || [],
            failures: response.data.failures || [],
            error: response.data.error || null
          };
        }
        throw new Error('Invalid response format');
      } catch (error) {
        console.error('Property Extraction Error:', error.message);
        
        if (error.response) {
          const { status, data } = error.response;
          const errorMessage = data?.error || 'Error extracting property data';
          
          // Handle 422 status with retry logic
          if (status === 422) {
            const elapsedTime = Date.now() - startTime;
            if (elapsedTime >= 30000) {
              throw new Error('Timeout: Property extraction failed after 30 seconds');
            }
            await new Promise(resolve => setTimeout(resolve, 1000));
            return makeRequest(retryCount + 1, startTime);
          }
          
          if (status === 404) {
            throw new Error(`File not found: ${errorMessage}`);
          } else if (status === 400) {
            throw new Error(`Invalid request: ${errorMessage}`);
          } else if (status === 500) {
            throw new Error(`Processing error: ${errorMessage}`);
          }
        }
        throw new Error(`Unknown error: ${error.message}`);
      }
    };

    return makeRequest();
  },

  transferSurveyOwnership: ({
    surveyIds,
    transferToUserId,
    logoFields = {},
  }) => {
    return fetch(`${process.env.APP_API_URL}/surveys/transfer_ownership/`, {
      method: 'POST',
      headers: {
        Authorization: Api.baseOptions.headers.Authorization,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        survey_ids: surveyIds,
        transfer_to_user_id: transferToUserId,
        ...logoFields,
      }),
    }).then((response) =>
      Promise.all([Promise.resolve(response), response.json()])
    )
  },

  getSkinFromS3: ({ subDomain }) => {
    const bucket = process.env.APP_SKINS_BUCKET
    return fetch(
      `https://${bucket}.s3.us-east-2.amazonaws.com/${subDomain}.json`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    )
      .then((response) =>
        Promise.all([
          Promise.resolve(response),
          response.ok ? response.json() : response.text(),
        ])
      )
      .then(([response, data]) => {
        if (!response.ok) {
          if (response.status === 403) {
            captureSentryMessageWarning({
              message: 'Missing skin in S3',
              tags: {
                subDomain,
                bucket,
              },
              extras: {
                response_status: response.status,
                data,
              },
            })
          } else {
            captureSentryMessageError({
              message: 'Error fetching skin from S3',
              tags: {
                subDomain,
                bucket,
              },
              extras: {
                response_status: response ? response.status : '',
                data,
              },
            })
          }
        }
        return [response, data]
      })
      .catch((exception) => {
        captureSentryMessageWarning({
          message: 'Error parsing skin response from S3',
          tags: {
            subDomain,
            bucket,
          },
          extras: {
            exception,
          },
        })
        return [null, null]
      })
  },

  updateCompany: ({ companyId, companyDetails }) => {
    return fetch(`${process.env.APP_API_URL}/company/${companyId}/`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        Authorization: Api.baseOptions.headers.Authorization,
      },
      body: JSON.stringify(companyDetails),
    }).then((response) =>
      Promise.all([
        Promise.resolve(response),
        response.ok ? response.json() : response.text(),
      ])
    )
  },
}
